import React, { useState, useMemo, useCallback } from 'react';
import { css } from '@emotion/react';
import {
  TextButton,
  Button,
  IconButton,
  IconSearch,
  IconChevronDown,
  IconEllipsis,
  IconPlus,
  IconArrowDown,
  IconCross,
  IconCheck,
  Pagination,
  Spinner,
} from '@flodesk/grain';
import Header from '@/components/app/Header';
import FilterModal from '@/components/app/FilterModal';

const pageStyles = css`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
`;

const contentStyles = css`
  flex: 1;
  background: var(--grn-color-background2);
  padding: 72px;
`;

const contentInnerStyles = css`
  display: flex;
  flex-direction: column;
  gap: 56px;
  max-width: 1140px;
  margin: 0 auto;
`;

const pageHeaderStyles = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 40px;
`;

const tabsStyles = css`
  display: flex;
  align-items: center;
  gap: 24px;
  font-family: 'Flodesk Sans', sans-serif;
  font-weight: 500;
  font-size: 24px;
  line-height: 28.6px;
  white-space: nowrap;
`;

const tabActiveStyles = css`
  color: var(--grn-color-content);
  cursor: pointer;
`;

const tabInactiveStyles = css`
  color: var(--grn-color-content2);
  cursor: pointer;

  &:hover {
    color: var(--grn-color-content);
  }
`;

const filtersStyles = css`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
`;

const buttonWrapperStyles = css`
  padding-left: 8px;
`;

const tableStyles = css`
  display: flex;
  width: 100%;
`;

const columnStyles = css`
  display: flex;
  flex-direction: column;
  gap: 10px;
  align-items: flex-start;
`;

const subscriberColumnStyles = css`
  ${columnStyles};
  flex: 1;
`;

const statusColumnStyles = css`
  ${columnStyles};
  width: 144px;
`;

const activityColumnStyles = css`
  ${columnStyles};
  width: 136px;
  min-width: 160px;
`;

const salesColumnStyles = css`
  ${columnStyles};
  width: 104px;
`;

const segmentsColumnStyles = css`
  ${columnStyles};
  width: 320px;
  align-items: flex-end;
`;

const reasonColumnStyles = css`
  ${columnStyles};
  flex: 1;
`;

const reasonRowStyles = css`
  display: flex;
  align-items: center;
  height: 56px;
  padding-right: 8px;
  border-bottom: 1px solid var(--grn-color-shade3);
  font-family: 'Flodesk Sans', sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  color: var(--grn-color-content);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const rowsContainerStyles = css`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const rowStyles = css`
  display: flex;
  align-items: center;
  height: 56px;
  padding: 0 8px 0 0;
  border-bottom: 1px solid var(--grn-color-shade3);
  gap: 16px;
`;

const statusRowStyles = css`
  display: flex;
  align-items: center;
  height: 56px;
  padding: 20px 32px 20px 0;
  border-bottom: 1px solid var(--grn-color-shade3);
`;

const activityRowStyles = css`
  display: flex;
  align-items: center;
  height: 56px;
  border-bottom: 1px solid var(--grn-color-shade3);
  font-family: 'Flodesk Sans', sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  color: var(--grn-color-content);
`;

const salesRowStyles = css`
  display: flex;
  align-items: center;
  height: 56px;
  padding-right: 32px;
  border-bottom: 1px solid var(--grn-color-shade3);
  font-family: 'Flodesk Sans', sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  color: var(--grn-color-content);
`;

const segmentsRowStyles = css`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  height: 56px;
  padding-right: 8px;
  border-bottom: 1px solid var(--grn-color-shade3);
  gap: 8px;
  overflow: hidden;
`;

const emailStyles = css`
  font-family: 'Flodesk Sans', sans-serif;
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  color: var(--grn-color-content);
  white-space: nowrap;
`;

const nameStyles = css`
  font-family: 'Flodesk Sans', sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  color: var(--grn-color-content2);
  white-space: nowrap;
`;

const badgeBaseStyles = css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 28px;
  padding: 0 12px;
  border-radius: 112px;
  font-family: 'Flodesk Sans', sans-serif;
  font-weight: 500;
  font-size: 12px;
  line-height: 17.8px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const activeBadgeStyles = css`
  ${badgeBaseStyles};
  background: rgba(121, 169, 148, 0.3);
  color: #466d5c;
`;

const neutralBadgeStyles = css`
  ${badgeBaseStyles};
  background: var(--grn-color-shade4);
  color: var(--grn-color-content);
`;

const fadedBadgeStyles = css`
  ${badgeBaseStyles};
  background: var(--grn-color-shade3);
  color: var(--grn-color-content2);
`;

const segmentBadgeStyles = css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 28px;
  padding: 0 8px;
  border-radius: var(--grn-radius-xs);
  font-family: 'Flodesk Sans', sans-serif;
  font-weight: 500;
  font-size: 12px;
  line-height: 17.8px;
  letter-spacing: -0.12px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex-shrink: 0;
  color: var(--grn-color-content);
`;

const countBadgeStyles = css`
  ${segmentBadgeStyles};
  background: var(--grn-color-shade4);
  color: var(--grn-color-content);
  letter-spacing: -0.12px;
`;

const statusMenuWrapperStyles = css`
  position: relative;
`;

const statusMenuStyles = css`
  position: absolute;
  top: 100%;
  right: 0;
  z-index: 10;
  display: flex;
  flex-direction: column;
  width: 220px;
  padding: 8px 0;
  background: var(--grn-color-background);
  border-radius: var(--grn-radius-m);
  box-shadow:
    0px 1px 1px rgba(0, 0, 0, 0.05),
    0px 1.98px 3.06px rgba(0, 0, 0, 0.05),
    0px 5.88px 14.05px rgba(0, 0, 0, 0.09);
`;

const statusMenuGroupStyles = css`
  display: flex;
  flex-direction: column;
  padding: 0 8px;
`;

const statusMenuItemStyles = css`
  display: flex;
  align-items: center;
  gap: 8px;
  height: 40px;
  padding: 0 12px;
  border-radius: var(--grn-radius-s);
  font-family: 'Flodesk Sans', sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  white-space: nowrap;
  cursor: pointer;

  &:hover {
    background: var(--grn-color-shade3);
  }
`;

const statusMenuLabelStyles = css`
  color: var(--grn-color-content);
  flex-shrink: 0;
`;

const statusMenuCountStyles = css`
  flex: 1;
  color: var(--grn-color-content2);
  overflow: hidden;
  text-overflow: ellipsis;
`;

const statusMenuDividerStyles = css`
  padding: 8px 0;

  &::after {
    content: '';
    display: block;
    height: 1px;
    background: var(--grn-color-shade3);
  }
`;

const statusMenuCheckStyles = css`
  flex-shrink: 0;
  color: var(--grn-color-icon);
  display: flex;
  align-items: center;
  width: 18px;
  height: 18px;

  svg {
    width: 18px;
    height: 18px;
  }
`;

const loadingOverlayStyles = css`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 120px 0;
`;

const paginationWrapperStyles = css`
  display: flex;
  justify-content: center;
  padding-top: 24px;
`;

const linkStyles = css`
  font-family: 'Flodesk Sans', sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  color: var(--grn-color-content2);
  text-decoration: underline;
  cursor: pointer;
`;

type Subscriber = {
  email: string;
  name: string;
  status: string;
  statusType: 'active' | 'neutral' | 'faded';
  lastActivity: string;
  isResendLink?: boolean;
  totalSales: string;
  segments: { label: string; color: string }[];
  extraSegments?: number;
  dateArchived?: string;
  archiveReason?: string;
};

const PAGE_SIZE = 10;

const SUBSCRIBERS: Subscriber[] = [
  {
    email: 'jane@austen.com',
    name: 'Jane Austen',
    status: 'Active',
    statusType: 'active',
    lastActivity: 'Mar 12, 2026',
    totalSales: '$102.50',
    segments: [
      { label: 'Profile social form', color: '#e8c0fd' },
      { label: 'May promotion', color: '#def2ea' },
    ],
    extraSegments: 4,
  },
  {
    email: 'mark.twain@example.com',
    name: 'Mark Twain',
    status: 'Active',
    statusType: 'active',
    lastActivity: 'Mar 10, 2026',
    totalSales: '$76.30',
    segments: [
      { label: 'Profile social form', color: '#e8c0fd' },
      { label: 'May promotion', color: '#def2ea' },
    ],
  },
  {
    email: 'sofia.ramirez@inbox.com',
    name: 'Sofia Ramirez',
    status: 'Active',
    statusType: 'active',
    lastActivity: 'Mar 18, 2026',
    totalSales: '$214.00',
    segments: [{ label: 'VIP customers', color: '#c5e1f5' }],
    extraSegments: 2,
  },
  {
    email: 'liam.chen@webmail.co',
    name: 'Liam Chen',
    status: 'Active',
    statusType: 'active',
    lastActivity: 'Mar 22, 2026',
    totalSales: '$48.90',
    segments: [
      { label: 'Profile social form', color: '#e8c0fd' },
      { label: 'Newsletter', color: '#cfddd7' },
    ],
  },
  {
    email: 'emma.johnson@mail.org',
    name: 'Emma Johnson',
    status: 'Active',
    statusType: 'active',
    lastActivity: 'Mar 15, 2026',
    totalSales: '$163.20',
    segments: [{ label: 'May promotion', color: '#def2ea' }],
  },
  {
    email: 'noah.williams@proton.me',
    name: 'Noah Williams',
    status: 'Active',
    statusType: 'active',
    lastActivity: 'Mar 20, 2026',
    totalSales: '$91.00',
    segments: [
      { label: 'Newsletter', color: '#cfddd7' },
      { label: 'May promotion', color: '#def2ea' },
    ],
  },
  {
    email: 'olivia.martinez@gmail.com',
    name: 'Olivia Martinez',
    status: 'Active',
    statusType: 'active',
    lastActivity: 'Mar 8, 2026',
    totalSales: '$330.75',
    segments: [{ label: 'VIP customers', color: '#c5e1f5' }],
    extraSegments: 3,
  },
  {
    email: 'ethan.kumar@outlook.com',
    name: 'Ethan Kumar',
    status: 'Active',
    statusType: 'active',
    lastActivity: 'Mar 19, 2026',
    totalSales: '$55.00',
    segments: [{ label: 'Profile social form', color: '#e8c0fd' }],
  },
  {
    email: 'ava.thompson@hey.com',
    name: 'Ava Thompson',
    status: 'Active',
    statusType: 'active',
    lastActivity: 'Mar 14, 2026',
    totalSales: '$127.40',
    segments: [
      { label: 'Newsletter', color: '#cfddd7' },
      { label: 'May promotion', color: '#def2ea' },
    ],
    extraSegments: 1,
  },
  {
    email: 'lucas.anderson@fastmail.com',
    name: 'Lucas Anderson',
    status: 'Active',
    statusType: 'active',
    lastActivity: 'Mar 21, 2026',
    totalSales: '$82.10',
    segments: [{ label: 'May promotion', color: '#def2ea' }],
  },
  {
    email: 'mia.garcia@yahoo.com',
    name: 'Mia Garcia',
    status: 'Active',
    statusType: 'active',
    lastActivity: 'Mar 11, 2026',
    totalSales: '$199.99',
    segments: [
      { label: 'VIP customers', color: '#c5e1f5' },
      { label: 'Profile social form', color: '#e8c0fd' },
    ],
  },
  {
    email: 'jackson.lee@icloud.com',
    name: 'Jackson Lee',
    status: 'Active',
    statusType: 'active',
    lastActivity: 'Mar 17, 2026',
    totalSales: '$64.50',
    segments: [{ label: 'Newsletter', color: '#cfddd7' }],
  },
  {
    email: 'isabella.clark@mail.com',
    name: 'Isabella Clark',
    status: 'Active',
    statusType: 'active',
    lastActivity: 'Mar 9, 2026',
    totalSales: '$145.80',
    segments: [{ label: 'May promotion', color: '#def2ea' }],
    extraSegments: 2,
  },
  {
    email: 'aiden.wright@zoho.com',
    name: 'Aiden Wright',
    status: 'Active',
    statusType: 'active',
    lastActivity: 'Mar 23, 2026',
    totalSales: '$38.25',
    segments: [{ label: 'Profile social form', color: '#e8c0fd' }],
  },
  {
    email: 'harper.king@proton.me',
    name: 'Harper King',
    status: 'Active',
    statusType: 'active',
    lastActivity: 'Mar 13, 2026',
    totalSales: '$256.00',
    segments: [
      { label: 'VIP customers', color: '#c5e1f5' },
      { label: 'Newsletter', color: '#cfddd7' },
    ],
  },
  {
    email: 'benjamin.scott@gmail.com',
    name: 'Benjamin Scott',
    status: 'Active',
    statusType: 'active',
    lastActivity: 'Mar 16, 2026',
    totalSales: '$71.90',
    segments: [{ label: 'May promotion', color: '#def2ea' }],
  },
  {
    email: 'charles.dickens@literature.net',
    name: 'Charles Dickens',
    status: 'Unconfirmed',
    statusType: 'neutral',
    lastActivity: 'Resend opt-in',
    isResendLink: true,
    totalSales: '$89.90',
    segments: [{ label: 'May promotion', color: '#efc5c7' }],
  },
  {
    email: 'virginia.woolf@mail.com',
    name: 'Virginia Woolf',
    status: 'Bounced',
    statusType: 'faded',
    lastActivity: 'Feb 28, 2026',
    totalSales: '$115.70',
    segments: [
      { label: 'Profile social form', color: '#fff98a' },
      { label: 'May promotion', color: '#cfddd7' },
    ],
  },
  {
    email: 'hemingway@writerhub.com',
    name: 'Ernest Hemingway',
    status: 'Archived',
    statusType: 'faded',
    lastActivity: 'Jan 15, 2026',
    totalSales: '-',
    segments: [
      { label: 'Profile social form', color: '#f5f5e4' },
      { label: 'May promotion', color: '#9ebbed' },
    ],
    extraSegments: 4,
    dateArchived: 'Feb 10, 2026',
    archiveReason: 'Archived via Welcome workflow',
  },
  {
    email: 'virginia.woolf@modernist.com',
    name: 'Virginia Woolf',
    status: 'Marked spam',
    statusType: 'faded',
    lastActivity: 'Mar 1, 2026',
    totalSales: '-',
    segments: [{ label: 'May promotion', color: '#ffdddb' }],
  },
];

function getStatusStyles(type: 'active' | 'neutral' | 'faded') {
  if (type === 'active') return activeBadgeStyles;
  if (type === 'neutral') return neutralBadgeStyles;
  return fadedBadgeStyles;
}

function StatusBadge({ status, type }: { status: string; type: 'active' | 'neutral' | 'faded' }) {
  return <span css={getStatusStyles(type)}>{status}</span>;
}

type SortColumn = 'subscriber' | 'status' | 'lastActivity' | 'totalSales' | 'segments';

function parseDate(dateStr: string): number {
  const d = new Date(dateStr);
  return Number.isNaN(d.getTime()) ? 0 : d.getTime();
}

function parseSales(salesStr: string): number {
  if (salesStr === '-') return 0;
  return parseFloat(salesStr.replace('$', '')) || 0;
}

function sortSubscribers(list: Subscriber[], column: SortColumn, ascending: boolean): Subscriber[] {
  const sorted = [...list];
  sorted.sort((a, b) => {
    let cmp = 0;
    switch (column) {
      case 'subscriber':
        cmp = a.email.localeCompare(b.email);
        break;
      case 'status':
        cmp = a.status.localeCompare(b.status);
        break;
      case 'lastActivity':
        cmp = parseDate(a.lastActivity) - parseDate(b.lastActivity);
        break;
      case 'totalSales':
        cmp = parseSales(a.totalSales) - parseSales(b.totalSales);
        break;
      default:
        return 0;
    }
    return ascending ? cmp : -cmp;
  });
  return sorted;
}

export default function Subscribers() {
  const [sortColumn, setSortColumn] = useState<SortColumn>('subscriber');
  const [ascending, setAscending] = useState(false);
  const [filterOpen, setFilterOpen] = useState(false);
  const [activeFilters, setActiveFilters] = useState<string[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [statusMenuOpen, setStatusMenuOpen] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [slowMode, setSlowMode] = useState(true);

  const triggerLoading = useCallback(() => {
    if (!slowMode) return undefined;
    setLoading(true);
    const timer = setTimeout(() => setLoading(false), 1000);
    return () => clearTimeout(timer);
  }, [slowMode]);

  const statusCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    SUBSCRIBERS.forEach(s => {
      counts[s.status] = (counts[s.status] || 0) + 1;
    });
    return counts;
  }, []);

  const handleStatusSelect = (status: string | null) => {
    setSelectedStatus(status);
    setCurrentPage(1);
    setStatusMenuOpen(false);
    triggerLoading();
  };

  const handleSort = (column: SortColumn) => {
    if (column === 'segments') return;
    if (sortColumn === column) {
      setAscending(!ascending);
    } else {
      setSortColumn(column);
      setAscending(true);
    }
  };

  const filtered = useMemo(() => {
    if (activeFilters.length > 0) {
      return SUBSCRIBERS.filter(s => activeFilters.includes(s.status));
    }
    if (selectedStatus) {
      return SUBSCRIBERS.filter(s => s.status === selectedStatus);
    }
    return SUBSCRIBERS;
  }, [activeFilters, selectedStatus]);

  const sorted = useMemo(
    () => sortSubscribers(filtered, sortColumn, ascending),
    [filtered, sortColumn, ascending],
  );

  const totalPages = Math.ceil(sorted.length / PAGE_SIZE);
  const paged = useMemo(
    () => sorted.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE),
    [sorted, currentPage],
  );

  const isArchivedView =
    selectedStatus === 'Archived' ||
    (activeFilters.length === 1 && activeFilters[0] === 'Archived');

  const arrowIcon = <IconArrowDown />;

  return (
    <div css={pageStyles} className="grn-context">
      <Header
        logoVariant={slowMode ? 'slowdesk' : 'flodesk'}
        onLogoClick={() => {
          setSlowMode(prev => !prev);
          if (slowMode) setLoading(false);
        }}
      />
      <div css={contentStyles}>
        <div css={contentInnerStyles}>
          <div css={pageHeaderStyles}>
            <div css={tabsStyles}>
              <span css={tabActiveStyles}>Subscribers</span>
              <span css={tabInactiveStyles}>Customers</span>
              <span css={tabInactiveStyles}>Segments</span>
            </div>
            <div css={filtersStyles}>
              <TextButton icon={<IconSearch />}>Search</TextButton>
              <TextButton icon={<IconChevronDown />} iconPosition="right">
                Sort
              </TextButton>
              <div css={statusMenuWrapperStyles}>
                <TextButton
                  icon={<IconChevronDown />}
                  iconPosition="right"
                  onClick={() => setStatusMenuOpen(!statusMenuOpen)}
                >
                  Status
                </TextButton>
                {statusMenuOpen && (
                  <div css={statusMenuStyles}>
                    <div css={statusMenuGroupStyles}>
                      <div
                        css={statusMenuItemStyles}
                        role="button"
                        tabIndex={0}
                        onClick={() => handleStatusSelect(null)}
                        onKeyDown={e => e.key === 'Enter' && handleStatusSelect(null)}
                      >
                        <span css={statusMenuLabelStyles}>All</span>
                        <span css={statusMenuCountStyles}>{SUBSCRIBERS.length}</span>
                        {selectedStatus === null && (
                          <span css={statusMenuCheckStyles}>
                            <IconCheck />
                          </span>
                        )}
                      </div>
                      {[
                        'Active',
                        'Unconfirmed',
                        'Bounced',
                        'Unsubscribed',
                        'Marked spam',
                        'Cleaned',
                      ].map(status => (
                        <div
                          key={status}
                          css={statusMenuItemStyles}
                          role="button"
                          tabIndex={0}
                          onClick={() => handleStatusSelect(status)}
                          onKeyDown={e => e.key === 'Enter' && handleStatusSelect(status)}
                        >
                          <span css={statusMenuLabelStyles}>{status}</span>
                          <span css={statusMenuCountStyles}>{statusCounts[status] || 0}</span>
                          {selectedStatus === status && (
                            <span css={statusMenuCheckStyles}>
                              <IconCheck />
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                    <div css={statusMenuDividerStyles} />
                    <div css={statusMenuGroupStyles}>
                      <div
                        css={statusMenuItemStyles}
                        role="button"
                        tabIndex={0}
                        onClick={() => handleStatusSelect('Archived')}
                        onKeyDown={e => e.key === 'Enter' && handleStatusSelect('Archived')}
                      >
                        <span css={statusMenuLabelStyles}>Archived</span>
                        <span css={statusMenuCountStyles}>{statusCounts.Archived || 0}</span>
                        {selectedStatus === 'Archived' && (
                          <span css={statusMenuCheckStyles}>
                            <IconCheck />
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
              {activeFilters.length > 0 ? (
                <TextButton
                  icon={<IconCross />}
                  iconPosition="right"
                  onClick={() => {
                    setActiveFilters([]);
                    setCurrentPage(1);
                    triggerLoading();
                  }}
                >
                  {sorted.length} results
                </TextButton>
              ) : (
                <TextButton onClick={() => setFilterOpen(true)}>Filter</TextButton>
              )}
              <IconButton icon={<IconEllipsis />} />
              <div css={buttonWrapperStyles}>
                <Button variant="accent" icon={<IconPlus />}>
                  Add subscriber
                </Button>
              </div>
            </div>
          </div>

          {loading ? (
            <div css={loadingOverlayStyles}>
              <Spinner
                css={css`
                  width: 24px;
                  height: 24px;
                `}
                size={24}
              />
            </div>
          ) : (
            <>
              <div css={tableStyles}>
                <div css={subscriberColumnStyles}>
                  <TextButton
                    icon={sortColumn === 'subscriber' ? arrowIcon : undefined}
                    iconPosition="right"
                    trimSide="left"
                    onClick={() => handleSort('subscriber')}
                  >
                    Subscriber
                  </TextButton>
                  <div css={rowsContainerStyles}>
                    {paged.map(s => (
                      <div key={s.email} css={rowStyles}>
                        <span css={emailStyles}>{s.email}</span>
                        <span css={nameStyles}>{s.name}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div css={statusColumnStyles}>
                  <TextButton
                    icon={sortColumn === 'status' ? arrowIcon : undefined}
                    iconPosition="right"
                    trimSide="left"
                    onClick={() => handleSort('status')}
                  >
                    Status
                  </TextButton>
                  <div css={rowsContainerStyles}>
                    {paged.map(s => (
                      <div key={s.email} css={statusRowStyles}>
                        <StatusBadge status={s.status} type={s.statusType} />
                      </div>
                    ))}
                  </div>
                </div>

                {isArchivedView ? (
                  <>
                    <div css={activityColumnStyles}>
                      <TextButton trimSide="left">Date archived</TextButton>
                      <div css={rowsContainerStyles}>
                        {paged.map(s => (
                          <div key={s.email} css={activityRowStyles}>
                            <span>{s.dateArchived || '-'}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div css={reasonColumnStyles}>
                      <TextButton trimSide="left">Archive reason</TextButton>
                      <div css={rowsContainerStyles}>
                        {paged.map(s => (
                          <div key={s.email} css={reasonRowStyles}>
                            <span>{s.archiveReason || '-'}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    <div css={activityColumnStyles}>
                      <TextButton
                        icon={sortColumn === 'lastActivity' ? arrowIcon : undefined}
                        iconPosition="right"
                        trimSide="left"
                        onClick={() => handleSort('lastActivity')}
                      >
                        Last activity
                      </TextButton>
                      <div css={rowsContainerStyles}>
                        {paged.map(s => (
                          <div key={s.email} css={activityRowStyles}>
                            {s.isResendLink ? (
                              <span css={linkStyles}>Resend opt-in</span>
                            ) : (
                              <span>{s.lastActivity}</span>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>

                    <div css={salesColumnStyles}>
                      <TextButton
                        icon={sortColumn === 'totalSales' ? arrowIcon : undefined}
                        iconPosition="right"
                        trimSide="left"
                        onClick={() => handleSort('totalSales')}
                      >
                        Total sales
                      </TextButton>
                      <div css={rowsContainerStyles}>
                        {paged.map(s => (
                          <div key={s.email} css={salesRowStyles}>
                            <span>{s.totalSales}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div css={segmentsColumnStyles}>
                      <TextButton trimSide="left">Segments</TextButton>
                      <div css={rowsContainerStyles}>
                        {paged.map(s => (
                          <div key={s.email} css={segmentsRowStyles}>
                            {s.segments.map(seg => (
                              <span
                                key={seg.label}
                                css={segmentBadgeStyles}
                                style={{ background: seg.color }}
                              >
                                {seg.label}
                              </span>
                            ))}
                            {s.extraSegments && (
                              <span css={countBadgeStyles}>+ {s.extraSegments}</span>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  </>
                )}
              </div>

              {totalPages > 1 && (
                <div css={paginationWrapperStyles}>
                  <Pagination
                    amount={totalPages}
                    selected={currentPage}
                    onChange={setCurrentPage}
                  />
                </div>
              )}
            </>
          )}
        </div>
      </div>
      {filterOpen && (
        <FilterModal
          initialStatuses={activeFilters}
          onClose={() => setFilterOpen(false)}
          onApply={statuses => {
            setActiveFilters(statuses);
            setSelectedStatus(null);
            setCurrentPage(1);
            setFilterOpen(false);
            triggerLoading();
          }}
        />
      )}
    </div>
  );
}
